'use client';

import type { CartProductItem } from '@/features/cart/shared/types/cart.types';
import { Link } from '@/shared/components/link/link';
import { useTranslations } from 'next-intl';
import { GameImageDisplay } from '@/features/games/shared/components/game-image-display';
import { GamePriceDisplay } from '@/features/games/shared/components/game-price-display';
import { PriceExcludingWildMembership } from '@/shared/types/prices';
import { Button } from '@/shared/components/primitives/button';
import { useCheckoutHref } from '@/features/checkout/shared/hooks/use-checkout-href';

type CartPreviewProps = {
  items: CartProductItem[];
};

export const CartPreview = ({ items }: CartPreviewProps) => {
  const t = useTranslations('Cart');
  const checkoutHref = useCheckoutHref();
  const sizes = '60px';
  const imageSize = 'square';

  return (
    <div className='bg-background-panel text-text absolute top-10 right-0 z-50 mt-2 w-96 rounded-lg p-4 shadow-lg'>
      <div className='text-background-panel absolute -top-5 right-3'>
        <svg width={20} height={20} viewBox='0 0 8 8'>
          <path d='M0 8 L4 4 L8 8' fill='currentColor' />
        </svg>
      </div>
      {items.length === 0 ? (
        <p className='text-sm text-gray-500'>{t('emptyCart')}</p>
      ) : (
        <>
          <ul className='scrollbar-stable max-h-[60vh] overflow-y-auto pr-4'>
            {items.map((item) => (
              <li key={item.productEntityId} className='flex items-start gap-3 pb-3 text-base'>
                <div className='w-20'>
                  {item.gameData?.defaultImage && (
                    <GameImageDisplay
                      image={item.gameData?.defaultImage}
                      href={item.gameData!.path}
                      imageSize={imageSize}
                      sizes={sizes}
                    />
                  )}
                </div>
                <div className='flex w-full flex-1 items-start justify-between'>
                  <div>
                    <p className='text-text-subtle'>{item.gameData?.brand?.name}</p>
                    <p className='font-medium'>
                      <Link href={item.gameData?.path ?? '/'}>{item.gameData?.name}</Link>
                    </p>
                  </div>
                  <div className='text-right'>
                    {Boolean(item.price) && (
                      <GamePriceDisplay price={item.price as PriceExcludingWildMembership} />
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className='mt-1 flex gap-3'>
            <Button
              onClick={() => {
                window.location.href = checkoutHref;
              }}
              variant='primary'
              className='w-1/2 text-base'
            >
              {t('checkoutNow')}
            </Button>
            <Button href='/cart' variant='secondary' className='w-1/2 text-base'>
              {t('viewCart')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
